import React from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { FileList } from 'components/ui';

import { htmlDecode } from 'utils/global';

import { isOwner } from 'modules/chat/utils';
import { Message } from 'modules/chat/types/Message';

import MessageText from './MessageText';
import DisplayNameFormatter from 'components/DisplayNameFormatter';
import StandardTime from 'components/ui/Dates/StandardTime';

interface MessageContentProps extends Message {
  isFirstUnread?: boolean;
  showAuthor?: boolean;
  showFooter?: boolean;
}

const MessageContent: React.FC<MessageContentProps> = ({
  id,
  links,
  medias,
  content,
  reactions,
  createdBy,
  createdOn,
  showAuthor,
  showFooter,
  isFirstUnread,
}) => {
  const isCustomer = isOwner(createdBy);
  const hasReactions = Boolean(reactions?.length  && reactions?.length > 0);
  const flexPosition = isCustomer ? 'justify-end items-end' : 'justify-start items-start';

  return (
    <div id={id}>
      {isFirstUnread && (
        <div className="flex items-center">
          <span className="text-brand-default font-semibold my-6 mr-2">
            <FormattedMessage id="new" defaultMessage="New" />
          </span>
          <div className="h-px w-full bg-brand-default" />
        </div>
      )}
      {showAuthor && !createdBy?.scheduler && (
        <span className={classNames('flex items-center font-bold my-4', flexPosition)}>
          {isCustomer ? <FormattedMessage id="you" defaultMessage="You" /> : <DisplayNameFormatter user={createdBy} />}
        </span>
      )}
      <div data-testid={`message-${id}`} className={classNames('flex flex-col gap-3', flexPosition)}>
        {Boolean(medias?.length && medias?.length > 0) && (
          <FileList
            hideFilename
            medias={medias!}
            className={classNames(
              '[&>div]:size-4xl [&>div]:!m-0 [&>div>div]:!bg-neutral-50 gap-1 w-full',
              flexPosition,
              medias!.length === 1 ? '[&>div]:size-5xl' : '[&>div]:size-4xl',
            )}
          />
        )}
        {content && (
          <MessageText
            links={links}
            content={htmlDecode(content)}
            className={classNames('overflow-hidden whitespace-pre-wrap break-words max-w-full rounded-card px-5 py-2', {
              'bg-brand-default text-white': isCustomer,
              'bg-card-bg': !isCustomer,
            })}
          />
        )}
      </div>
      {hasReactions && (
        <div className="relative min-h-6">
          <div className={classNames('flex', flexPosition, 'absolute -top-2 w-full text-xxs gap-0')} key={id}>
            {reactions!.map(({ id, emoji }) => (
              <div className="rounded-full px-2 py-1 bg-brand-50 border border-white leading-none" key={id}>
                {emoji}
              </div>
            ))}
          </div>
        </div>
      )}
      {showFooter && (
        <div className={classNames('flex', flexPosition, !hasReactions && 'mt-2')}>
          <StandardTime value={new Date(createdOn)} />
        </div>
      )}
    </div>
  );
};

export default MessageContent;
